/**
 * Blended WHLS Fee Calculator for NC Template
 * 
 * This file contains the calculation logic for Blended WHLS Fee for NC template based on:
 * - Usage Period Start Date (calculateNCBlendedWhlsFee)
 * - Target Launch Date (whlsFeeCalculationNC)
 * - Suggested Bid Price
 * - VA Annual NC EST
 * - DOD Annual NC EST
 * - IHS Annual NC EST
 * - BOP Annual NC EST
 * 
 * The calculation uses different rate schedules depending on whether the start date
 * is after 8/1/2024, and calculates a weighted average based on VA/IHS/BOP and DOD usage.
 */

/**
 * Calculate Blended WHLS Fee for NC Template
 * 
 * @param usagePeriodStartDate - Usage period start date (Date object or string)
 * @param suggestedBidPrice - Suggested bid price
 * @param vaAnnualNcEst - VA Annual NC EST
 * @param dodAnnualNcEst - DOD Annual NC EST
 * @param ihsAnnualNcEst - IHS Annual NC EST
 * @param bopAnnualNcEst - BOP Annual NC EST
 * @returns The calculated blended WHLS fee amount
 */
export const calculateNCBlendedWhlsFee = (
  usagePeriodStartDate: Date | string | null | undefined,
  suggestedBidPrice: number,
  vaAnnualNcEst: number,
  dodAnnualNcEst: number,
  ihsAnnualNcEst: number,
  bopAnnualNcEst: number
): number => {
  // Validate inputs
  if (!usagePeriodStartDate || !suggestedBidPrice || suggestedBidPrice <= 0) {
    return 0;
  }

  // Parse usage period start date
  const startDate = parseDate(usagePeriodStartDate);
  if (!startDate) {
    return 0;
  }

  // Check if start date is after 8/1/2024
  const aug2024 = new Date(2024, 7, 1); // Month is 0-indexed, so 7 = August
  const isAfterAug2024 = startDate > aug2024;

  // Calculate total annual usage
  const totalUsage = vaAnnualNcEst + dodAnnualNcEst + ihsAnnualNcEst + bopAnnualNcEst;
  
  // If total usage is 0, return 0
  if (totalUsage === 0) {
    return 0;
  }

  // Component 1: VA + IHS + BOP
  const component1Sum = vaAnnualNcEst + ihsAnnualNcEst + bopAnnualNcEst;
  const component1Weight = component1Sum / totalUsage;
  const component2Weight = dodAnnualNcEst / totalUsage;

  // Get rates based on start date
  let component1Rate: number;
  let component2Rate: number;

  if (isAfterAug2024) {
    component1Rate = calculateEscalatingRateA(startDate);
    component2Rate = getDODAnnualNcEstRate(startDate);
  } else {
    component1Rate = calculateEscalatingRateB(startDate);
    component2Rate = getDODAnnualNcEstRate(startDate);
  }

  // Calculate weighted average rate
  const weightedAverageRate = (component1Weight * component1Rate) + (component2Weight * component2Rate);

  // Final calculation: Suggested Bid Price * Weighted Average Rate
  return suggestedBidPrice * weightedAverageRate;
};

/**
 * Parse date from various formats
 */
const parseDate = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;

  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }

  if (typeof date === 'string') {
    // Try parsing MM-DD-YYYY format first
    const mmddyyyyMatch = date.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (mmddyyyyMatch) {
      const [, month, day, year] = mmddyyyyMatch;
      const parsed = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    
    // Try standard Date parsing
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

/**
 * Get DOD Annual NC EST rate (same for both schedules)
 */
const getDODAnnualNcEstRate = (startDate: Date): number => {
  const dec31_2025 = new Date(2025, 11, 31); // December 31, 2025
  const jan1_2026 = new Date(2026, 0, 1); // January 1, 2026

  // Calculate end of period (12 months after start date)
  const endOfPeriod = new Date(startDate);
  endOfPeriod.setMonth(endOfPeriod.getMonth() + 12);
  endOfPeriod.setDate(endOfPeriod.getDate() - 1);

  if (startDate <= dec31_2025) {
    if (endOfPeriod <= dec31_2025) {
      return 0.03; // 3%
    } else if (endOfPeriod >= jan1_2026) {
      // Calculate weighted average across year boundary
      const daysIn2025 = Math.max(0, (jan1_2026.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysIn2026 = Math.max(0, (endOfPeriod.getTime() - jan1_2026.getTime()) / (1000 * 60 * 60 * 24) + 1);
      const totalDays = daysIn2025 + daysIn2026;

      if (totalDays === 0) {
        return 0.0325; // 3.25%
      }

      const daysIn2025Percent = daysIn2025 / totalDays;
      const blendedRate = (daysIn2025Percent * 3 + (1 - daysIn2025Percent) * 3.25) / 100;
      
      // Round up to 4 decimal places
      return Math.ceil(blendedRate * 10000) / 10000;
    } else {
      return 0.0325; // 3.25%
    }
  } else {
    return 0.0325; // 3.25%
  }
};

/**
 * Calculate Escalating Rate for Schedule A (after Aug 1, 2024)
 */
const calculateEscalatingRateA = (startDate: Date): number => {
  const anniversaryDate = new Date(startDate);
  anniversaryDate.setFullYear(anniversaryDate.getFullYear() + 1);

  const apr30_2023 = new Date(2023, 3, 30);
  const apr30_2024 = new Date(2024, 3, 30);
  const apr30_2025 = new Date(2025, 3, 30);
  const apr30_2026 = new Date(2026, 3, 30);
  const apr30_2027 = new Date(2027, 3, 30);
  const apr30_2028 = new Date(2028, 3, 30);
  const may1_2024 = new Date(2024, 4, 1);
  const may1_2025 = new Date(2025, 4, 1);
  const may1_2026 = new Date(2026, 4, 1);
  const may1_2027 = new Date(2027, 4, 1);
  const may1_2028 = new Date(2028, 4, 1);

  if (startDate <= apr30_2023) {
    const daysUntilMilestone = Math.max(0, (apr30_2024.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    if (apr30_2024 <= anniversaryDate) {
      return Math.round((daysUntilMilestone / 365) * 1 * 10) / 10 / 100; // 1%
    } else {
      return Math.round(Math.min(daysUntilMilestone / 365, 1) * 1 * 10) / 10 / 100;
    }
  } else if (startDate <= apr30_2024) {
    const daysAt1Pct = Math.max(0, (apr30_2024.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysAt5_5Pct = Math.max(0, (anniversaryDate.getTime() - may1_2024.getTime()) / (1000 * 60 * 60 * 24) + 1);
    return Math.round(((daysAt1Pct / 365 * 1) + (daysAt5_5Pct / 365 * 5.5)) * 10) / 10 / 100;
  } else if (startDate <= apr30_2025) {
    const daysAt5_5Pct = Math.max(0, (apr30_2025.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysAt6_5Pct = Math.max(0, (anniversaryDate.getTime() - may1_2025.getTime()) / (1000 * 60 * 60 * 24) + 1);
    return Math.round(((daysAt5_5Pct / 365 * 5.5) + (daysAt6_5Pct / 365 * 6.5)) * 10) / 10 / 100;
  } else if (startDate <= apr30_2026) {
    const daysAt6_5Pct = Math.max(0, (apr30_2026.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysAt7_5Pct = Math.max(0, (anniversaryDate.getTime() - may1_2026.getTime()) / (1000 * 60 * 60 * 24) + 1);
    return Math.round(((daysAt6_5Pct / 365 * 6.5) + (daysAt7_5Pct / 365 * 7.5)) * 10) / 10 / 100;
  } else if (startDate <= apr30_2027) {
    const daysAt7_5Pct = Math.max(0, (apr30_2027.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysAt7_5PctNext = Math.max(0, (anniversaryDate.getTime() - may1_2027.getTime()) / (1000 * 60 * 60 * 24) + 1);
    return Math.round(((daysAt7_5Pct / 365 * 7.5) + (daysAt7_5PctNext / 365 * 7.5)) * 10) / 10 / 100;
  } else if (startDate <= apr30_2028) {
    const daysAt7_5Pct = Math.max(0, (apr30_2028.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysAt8_5Pct = Math.max(0, (anniversaryDate.getTime() - may1_2028.getTime()) / (1000 * 60 * 60 * 24) + 1);
    return Math.round(((daysAt7_5Pct / 365 * 7.5) + (daysAt8_5Pct / 365 * 8.5)) * 10) / 10 / 100;
  } else {
    return 0.085; // 8.5%
  }
};

/**
 * Calculate Escalating Rate for Schedule B (on or before Aug 1, 2024)
 */
const calculateEscalatingRateB = (startDate: Date): number => {
  const anniversaryDate = new Date(startDate);
  anniversaryDate.setFullYear(anniversaryDate.getFullYear() + 1);

  const apr30_2023 = new Date(2023, 3, 30);
  const apr30_2024 = new Date(2024, 3, 30);
  const apr30_2025 = new Date(2025, 3, 30);
  const apr30_2026 = new Date(2026, 3, 30);
  const apr30_2027 = new Date(2027, 3, 30);
  const apr30_2028 = new Date(2028, 3, 30);
  const may1_2024 = new Date(2024, 4, 1);
  const may1_2025 = new Date(2025, 4, 1);
  const may1_2026 = new Date(2026, 4, 1);
  const may1_2027 = new Date(2027, 4, 1);
  const may1_2028 = new Date(2028, 4, 1);

  if (startDate <= apr30_2023) {
    const daysUntilMilestone = Math.max(0, (apr30_2024.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    if (apr30_2024 <= anniversaryDate) {
      return Math.round((daysUntilMilestone / 365) * 1 * 10) / 10 / 100; // 1%
    } else {
      return Math.round(Math.min(daysUntilMilestone / 365, 1) * 1 * 10) / 10 / 100;
    }
  } else if (startDate <= apr30_2024) {
    const daysAt1Pct = Math.max(0, (apr30_2024.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysAt7_5Pct = Math.max(0, (anniversaryDate.getTime() - may1_2024.getTime()) / (1000 * 60 * 60 * 24) + 1);
    return Math.round(((daysAt1Pct / 365 * 1) + (daysAt7_5Pct / 365 * 7.5)) * 10) / 10 / 100;
  } else if (startDate <= apr30_2025) {
    const daysAt7_5Pct = Math.max(0, (apr30_2025.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysAt7_5PctNext = Math.max(0, (anniversaryDate.getTime() - may1_2025.getTime()) / (1000 * 60 * 60 * 24) + 1);
    return Math.round(((daysAt7_5Pct / 365 * 7.5) + (daysAt7_5PctNext / 365 * 7.5)) * 10) / 10 / 100;
  } else if (startDate <= apr30_2026) {
    const daysAt7_5Pct = Math.max(0, (apr30_2026.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysAt7_5PctNext = Math.max(0, (anniversaryDate.getTime() - may1_2026.getTime()) / (1000 * 60 * 60 * 24) + 1);
    return Math.round(((daysAt7_5Pct / 365 * 7.5) + (daysAt7_5PctNext / 365 * 7.5)) * 10) / 10 / 100;
  } else if (startDate <= apr30_2027) {
    const daysAt7_5Pct = Math.max(0, (apr30_2027.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysAt8_5Pct = Math.max(0, (anniversaryDate.getTime() - may1_2027.getTime()) / (1000 * 60 * 60 * 24) + 1);
    return Math.round(((daysAt7_5Pct / 365 * 7.5) + (daysAt8_5Pct / 365 * 8.5)) * 10) / 10 / 100;
  } else if (startDate <= apr30_2028) {
    const daysAt8_5Pct = Math.max(0, (apr30_2028.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysAt9_5Pct = Math.max(0, (anniversaryDate.getTime() - may1_2028.getTime()) / (1000 * 60 * 60 * 24) + 1);
    return Math.round(((daysAt8_5Pct / 365 * 8.5) + (daysAt9_5Pct / 365 * 9.5)) * 10) / 10 / 100;
  } else {
    return 0.095; // 9.5%
  }
};

/**
 * Calculate Blended Whole Seller Fee for NC Template based on Target Launch Date
 * 
 * This function calculates a weighted, date-adjusted bid price using annual estimates
 * from multiple government agencies (VA, DOD, IHS, BOP) and applies different interest
 * rate schedules based on the target launch date.
 * 
 * @param targetLaunchDate - Target launch date (Date object or string YYYY-MM-DD)
 * @param suggestedBidPrice - The base suggested bid price to be adjusted
 * @param vaAnnualNCEst - VA (Veterans Affairs) Annual Net Cost Estimate
 * @param dodAnnualNCEst - DOD (Department of Defense) Annual Net Cost Estimate
 * @param ihsAnnualNCEst - IHS (Indian Health Service) Annual Net Cost Estimate
 * @param bopAnnualNCEst - BOP (Bureau of Prisons) Annual Net Cost Estimate
 * @returns The calculated adjusted bid price (Blended Whole Seller Fee)
 */
export const whlsFeeCalculationNC = (
  targetLaunchDate: Date | string | null | undefined,
  suggestedBidPrice: number,
  vaAnnualNCEst: number,
  dodAnnualNCEst: number,
  ihsAnnualNCEst: number,
  bopAnnualNCEst: number
): number => {
  // Validate inputs
  if (!targetLaunchDate || !suggestedBidPrice || suggestedBidPrice <= 0) {
    return 0;
  }

  // Parse target launch date
  const launchDate = parseDate(targetLaunchDate);
  if (!launchDate) {
    return 0;
  }

  // Step 1: Calculate total annual estimates = VA + IHS + BOP + DOD
  const totalAnnualEstimates = vaAnnualNCEst + dodAnnualNCEst + ihsAnnualNCEst + bopAnnualNCEst;
  
  // Handle division by zero
  if (totalAnnualEstimates === 0) {
    return 0;
  }

  // Step 2: Calculate Group 1 total = VA + IHS + BOP
  const group1Total = vaAnnualNCEst + ihsAnnualNCEst + bopAnnualNCEst;
  
  // Step 3: Calculate Group 2 total = DOD
  const group2Total = dodAnnualNCEst;

  // Step 4: Calculate Weight 1 = Group 1 Total / Total Annual Estimates
  const weight1 = group1Total / totalAnnualEstimates;
  
  // Step 5: Calculate Weight 2 = Group 2 Total / Total Annual Estimates
  const weight2 = group2Total / totalAnnualEstimates;

  // Step 6: Determine if target launch date > August 1, 2024 to choose rate path
  const thresholdDate = new Date(2024, 7, 1); // August 1, 2024 (month is 0-indexed)
  const isAfterAug2024 = launchDate > thresholdDate;

  // Step 7: Calculate Group 1 percentage based on target launch date and selected rate schedule
  let group1Percentage: number;
  if (isAfterAug2024) {
    group1Percentage = calculateGroup1RatePath1(launchDate);
  } else {
    group1Percentage = calculateGroup1RatePath2(launchDate);
  }

  // Step 8: Calculate Group 2 percentage based on target launch date and selected rate schedule
  const group2Percentage = calculateGroup2Rate(launchDate);

  // Step 9: Calculate combined percentage = (Weight 1 × Group 1 Percentage) + (Weight 2 × Group 2 Percentage)
  const combinedPercentage = (weight1 * group1Percentage) + (weight2 * group2Percentage);

  // Step 10: Calculate final result = Suggested Bid Price × Combined Percentage
  return suggestedBidPrice * combinedPercentage;
};

/**
 * Calculate Group 1 Rate for Path 1 (launch date after August 1, 2024)
 * Rate schedule: 1%, 1%/7.5%, 7.5%/7.5%, 7.5%/7.5%, 7.5%/8.5%, 8.5%/9.5%, 9.5%
 */
const calculateGroup1RatePath1 = (launchDate: Date): number => {
  // Calculate 1-year anniversary date from launch date
  const anniversaryDate = new Date(launchDate);
  anniversaryDate.setFullYear(anniversaryDate.getFullYear() + 1);

  // Rate transition dates
  const apr30_2023 = new Date(2023, 3, 30); // April 30, 2023
  const apr30_2024 = new Date(2024, 3, 30); // April 30, 2024
  const apr30_2025 = new Date(2025, 3, 30); // April 30, 2025
  const apr30_2026 = new Date(2026, 3, 30); // April 30, 2026
  const apr30_2027 = new Date(2027, 3, 30); // April 30, 2027
  const apr30_2028 = new Date(2028, 3, 30); // April 30, 2028
  const may1_2024 = new Date(2024, 4, 1); // May 1, 2024
  const may1_2025 = new Date(2025, 4, 1); // May 1, 2025
  const may1_2026 = new Date(2026, 4, 1); // May 1, 2026
  const may1_2027 = new Date(2027, 4, 1); // May 1, 2027
  const may1_2028 = new Date(2028, 4, 1); // May 1, 2028

  if (launchDate <= apr30_2023) {
    // before_2023_04_30: 0.01 (1%)
    const daysUntilApr30_2024 = Math.max(0, (apr30_2024.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = apr30_2024 <= anniversaryDate 
      ? Math.round((daysUntilApr30_2024 / 365) * 1 * 10) / 10 / 100
      : Math.round(Math.min(daysUntilApr30_2024 / 365, 1) * 1 * 10) / 10 / 100;
    return rate;
  } else if (launchDate <= apr30_2024) {
    // 2023_04_30_to_2024_04_30: period1: 0.01, period2: 0.075
    const daysInPeriod1 = Math.max(0, (apr30_2024.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2024.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 1) + (daysInPeriod2 / 365 * 7.5);
    const roundedRate = Math.round(rate * 10) / 10 / 100;
    return roundedRate;
  } else if (launchDate <= apr30_2025) {
    // 2024_04_30_to_2025_04_30: period1: 0.075, period2: 0.075
    const daysInPeriod1 = Math.max(0, (apr30_2025.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2025.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 7.5) + (daysInPeriod2 / 365 * 7.5);
    const roundedRate = Math.round(rate * 10) / 10 / 100;
    return roundedRate;
  } else if (launchDate <= apr30_2026) {
    // 2025_04_30_to_2026_04_30: period1: 0.075, period2: 0.075
    const daysInPeriod1 = Math.max(0, (apr30_2026.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2026.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 7.5) + (daysInPeriod2 / 365 * 7.5);
    const roundedRate = Math.round(rate * 10) / 10 / 100;
    return roundedRate;
  } else if (launchDate <= apr30_2027) {
    // 2026_04_30_to_2027_04_30: period1: 0.075, period2: 0.085
    const daysInPeriod1 = Math.max(0, (apr30_2027.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2027.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 7.5) + (daysInPeriod2 / 365 * 8.5);
    const roundedRate = Math.round(rate * 10) / 10 / 100;
    return roundedRate;
  } else if (launchDate <= apr30_2028) {
    // 2027_04_30_to_2028_04_30: period1: 0.085, period2: 0.095
    const daysInPeriod1 = Math.max(0, (apr30_2028.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2028.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 8.5) + (daysInPeriod2 / 365 * 9.5);
    const roundedRate = Math.round(rate * 10) / 10 / 100;
    return roundedRate;
  } else {
    // after_2028_04_30: 0.095 (9.5%)
    return 0.095;
  }
};

/**
 * Calculate Group 1 Rate for Path 2 (launch date before or on August 1, 2024)
 * Rate schedule: 1%, 1%/5.5%, 5.5%/6.5%, 6.5%/7.5%, 7.5%/7.5%, 7.5%/8.5%, 8.5%
 */
const calculateGroup1RatePath2 = (launchDate: Date): number => {
  // Calculate 1-year anniversary date from launch date
  const anniversaryDate = new Date(launchDate);
  anniversaryDate.setFullYear(anniversaryDate.getFullYear() + 1);

  // Rate transition dates
  const apr30_2023 = new Date(2023, 3, 30); // April 30, 2023
  const apr30_2024 = new Date(2024, 3, 30); // April 30, 2024
  const apr30_2025 = new Date(2025, 3, 30); // April 30, 2025
  const apr30_2026 = new Date(2026, 3, 30); // April 30, 2026
  const apr30_2027 = new Date(2027, 3, 30); // April 30, 2027
  const apr30_2028 = new Date(2028, 3, 30); // April 30, 2028
  const may1_2024 = new Date(2024, 4, 1); // May 1, 2024
  const may1_2025 = new Date(2025, 4, 1); // May 1, 2025
  const may1_2026 = new Date(2026, 4, 1); // May 1, 2026
  const may1_2027 = new Date(2027, 4, 1); // May 1, 2027
  const may1_2028 = new Date(2028, 4, 1); // May 1, 2028

  if (launchDate <= apr30_2023) {
    // before_2023_04_30: 0.01 (1%)
    const daysUntilApr30_2024 = Math.max(0, (apr30_2024.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = apr30_2024 <= anniversaryDate 
      ? Math.round((daysUntilApr30_2024 / 365) * 1 * 10) / 10 / 100
      : Math.round(Math.min(daysUntilApr30_2024 / 365, 1) * 1 * 10) / 10 / 100;
    return rate;
  } else if (launchDate <= apr30_2024) {
    // 2023_04_30_to_2024_04_30: period1: 0.01, period2: 0.055
    const daysInPeriod1 = Math.max(0, (apr30_2024.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2024.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 1) + (daysInPeriod2 / 365 * 5.5);
    const roundedRate = Math.round(rate * 10) / 10 / 100;
    return roundedRate;
  } else if (launchDate <= apr30_2025) {
    // 2024_04_30_to_2025_04_30: period1: 0.055, period2: 0.065
    const daysInPeriod1 = Math.max(0, (apr30_2025.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2025.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 5.5) + (daysInPeriod2 / 365 * 6.5);
    const roundedRate = Math.round(rate * 10) / 10 / 100;
    return roundedRate;
  } else if (launchDate <= apr30_2026) {
    // 2025_04_30_to_2026_04_30: period1: 0.065, period2: 0.075
    const daysInPeriod1 = Math.max(0, (apr30_2026.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2026.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 6.5) + (daysInPeriod2 / 365 * 7.5);
    const roundedRate = Math.round(rate * 10) / 10 / 100;
    return roundedRate;
  } else if (launchDate <= apr30_2027) {
    // 2026_04_30_to_2027_04_30: period1: 0.075, period2: 0.075
    const daysInPeriod1 = Math.max(0, (apr30_2027.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2027.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 7.5) + (daysInPeriod2 / 365 * 7.5);
    const roundedRate = Math.round(rate * 10) / 10 / 100;
    return roundedRate;
  } else if (launchDate <= apr30_2028) {
    // 2027_04_30_to_2028_04_30: period1: 0.075, period2: 0.085
    const daysInPeriod1 = Math.max(0, (apr30_2028.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const daysInPeriod2 = Math.max(0, (anniversaryDate.getTime() - may1_2028.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const rate = (daysInPeriod1 / 365 * 7.5) + (daysInPeriod2 / 365 * 8.5);
    const roundedRate = Math.round(rate * 10) / 10 / 100;
    return roundedRate;
  } else {
    // after_2028_04_30: 0.085 (8.5%)
    return 0.085;
  }
};

/**
 * Calculate Group 2 Rate (DOD) based on target launch date
 * Uses 3% or 3.25% based on date ranges, with blended rate when 12 months crosses 2026-01-01
 */
const calculateGroup2Rate = (launchDate: Date): number => {
  const dodTransitionDate = new Date(2025, 11, 31); // December 31, 2025
  const dodRateChangeDate = new Date(2026, 0, 1); // January 1, 2026

  // Calculate 12-month period end date
  const endOf12Months = new Date(launchDate);
  endOf12Months.setMonth(endOf12Months.getMonth() + 12);
  endOf12Months.setDate(endOf12Months.getDate() - 1);

  if (launchDate <= dodTransitionDate) {
    // before_2025_12_31
    if (endOf12Months <= dodTransitionDate) {
      // if_12months_before_2025_12_31: 0.03 (3%)
      return 0.03;
    } else if (endOf12Months >= dodRateChangeDate) {
      // if_12months_after_2026_01_01: blended_3_to_3.25
      // Calculate percentage of year before 2026-01-01 at 3% and percentage after at 3.25%
      const daysIn2025 = Math.max(0, (dodRateChangeDate.getTime() - launchDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysIn2026 = Math.max(0, (endOf12Months.getTime() - dodRateChangeDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
      const totalDays = daysIn2025 + daysIn2026;

      if (totalDays === 0) {
        return 0.0325; // 3.25%
      }

      // Calculate blended rate: (daysIn2025 / totalDays × 3%) + (daysIn2026 / totalDays × 3.25%)
      const blendedRate = (daysIn2025 / totalDays * 3 + daysIn2026 / totalDays * 3.25) / 100;
      
      // Round up to 4 decimal places
      return Math.ceil(blendedRate * 10000) / 10000;
    } else {
      // if_12months_after_2025_12_31: 0.0325 (3.25%)
      return 0.0325;
    }
  } else {
    // after_2025_12_31: 0.0325 (3.25%)
    return 0.0325;
  }
};

